import { useState, useEffect } from 'react';
import { Student } from '@court-sync/shared';
import { useAuthStore } from '../store/auth';
import { useGuardianAcademyStore } from '../store/guardianAcademy';
import { studentService } from '../services/student';

export type UserScenario = 'parent' | 'self-student' | 'single-user' | 'loading';

export interface UserScenarioData {
  scenario: UserScenario;
  students: Student[];
  selfStudent: Student | null; // Student record that represents the user themselves
  children: Student[]; // Students that are children (not the user themselves)
  academyId?: string; // Active academy context from user (for multi-academy guardians)
}

/**
 * Hook to detect and manage user scenario:
 * - 'parent': User manages children (has students where parentId === userId)
 * - 'self-student': User is also a student (has a student record representing themselves)
 * - 'single-user': User has no student records, just manages their own account
 */
export function useUserScenario(): UserScenarioData {
  const { user } = useAuthStore();
  const { academyId: guardianAcademyId } = useGuardianAcademyStore();
  const [scenario, setScenario] = useState<UserScenario>('loading');
  const [students, setStudents] = useState<Student[]>([]);
  const [selfStudent, setSelfStudent] = useState<Student | null>(null);
  const [children, setChildren] = useState<Student[]>([]);

  useEffect(() => {
    if (!user) {
      // State is already initialized to 'loading', no need to set it again
      return;
    }

    const loadScenario = async () => {
      try {
        const academyId = guardianAcademyId ?? user.academyId;
        const response = await studentService.getStudents(academyId);
        const allStudents = response.data || [];

        setStudents(allStudents);

        // Determine if user is also a student themselves
        // Check if any student matches user's name closely (within reasonable age range)
        // For now, we'll use a simple heuristic: if there's exactly one student and
        // the names match, or if user explicitly marked themselves as a student
        // In the future, we could add an `isSelf` boolean field to Student model
        
        // Prefer explicit isSelf flag; fall back to name heuristic for legacy data
        const potentialSelfStudent = allStudents.find(student =>
          student.isSelf === true ||
          (() => {
            const studentName = `${student.firstName} ${student.lastName}`.toLowerCase();
            const userName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().trim();
            return studentName === userName ||
                   student.firstName.toLowerCase() === (user.firstName || '').toLowerCase();
          })()
        );

        // If we found a potential self-student, check if there are other students
        if (potentialSelfStudent && allStudents.length > 1) {
          // User is both a parent and a self-student
          setSelfStudent(potentialSelfStudent);
          setChildren(allStudents.filter(s => s.id !== potentialSelfStudent.id));
          setScenario('parent'); // Primary scenario is parent, but they're also a student
        } else if (potentialSelfStudent && allStudents.length === 1) {
          // User is only a self-student (managing themselves)
          setSelfStudent(potentialSelfStudent);
          setChildren([]);
          setScenario('self-student');
        } else if (allStudents.length > 0) {
          // User is a parent managing children
          setSelfStudent(null);
          setChildren(allStudents);
          setScenario('parent');
        } else {
          // User has no students - single user scenario
          setSelfStudent(null);
          setChildren([]);
          setScenario('single-user');
        }
      } catch (error) {
        console.error('Error loading user scenario:', error);
        // Default to single-user if there's an error
        setScenario('single-user');
        setStudents([]);
        setSelfStudent(null);
        setChildren([]);
      }
    };

    loadScenario();
  }, [user, guardianAcademyId]);

  return {
    scenario,
    students,
    selfStudent,
    children,
    academyId: guardianAcademyId ?? user?.academyId,
  };
}




